/**
 * Stable API error codes returned in HTTP JSON `{ statusCode, message, details? }`.
 * Frontend maps `message` to `common.error.<code>` when a translation exists.
 */
export const API_ERROR_CODES = [
  'UNAUTHORIZED',
  'INTERNAL_SERVER_ERROR',
  'CGU_NEVER_ACCEPTED',
  'CGU_NOT_ACCEPTED',
  'ACCOUNT_DEACTIVATED',
  'NO_ACCOUNT_FOUND',
  'STUDENT_ACCOUNT_EXISTS',
  'INVALID_GOOGLE_TOKEN',
  'GOOGLE_EMAIL_NOT_VERIFIED',
  'TICKET_REQUIRED',
  'INVALID_NUMBER_OF_TEACHERS',
  'ANALYTICS_PERIOD_REQUIRED',
  'COMPONENT_NOT_FOUND',
  'MISSING_REQUIRED_PARAMETERS',
  'INVALID_DATE_RANGE',
  'ERROR_FETCHING_RECORDS',
  'ERROR_FETCHING_RECORD',
  'ERROR_CREATING_RECORD',
  'ERROR_UPDATING_RECORD',
  'ERROR_DELETING_RECORD',
  'MISSING_PARAMETERS_OR_INVALID_IDS',
  'ERROR_DELETING_RECORDS',
  'ACTIVITY_NOT_FOUND',
  'ACTIVITY_NOT_FOUND_OR_ACCESS_DENIED',
  'DEADLINE_BEFORE_PUBLICATION',
  'ACTIVITY_SETTINGS_NOT_FOUND',
  'NO_SUBJECT_FILE_UPLOADED',
  'NO_RUBRIC_GENERATED',
  'NO_RUBRIC_CORRECTED_COPIES',
  'COPIES_CORRECTION_SETTINGS_NOT_FOUND',
  'COPIES_CORRECTION_NOT_FOUND',
  'COPIES_CORRECTION_OR_STUDENT_FORBIDDEN',
  'QR_CODE_EXPIRED',
  'HANDWRITTEN_COPY_NOT_FOUND',
  'RESULTS_NOT_YET_PUBLISHED',
  'RESULTS_ACCESS_DENIED',
  'TEACHER_NOT_FOUND',
  'STUDENT_LIST_NOT_FOUND',
  'STRUCTURE_NOT_GENERATED',
  'ERROR_FETCHING_STRUCTURE',
  'GRADED_STRUCTURE_NOT_GENERATED',
  'ERROR_FETCHING_GRADED_STRUCTURE',
  'ERROR_UPDATING_GRADED_STRUCTURE',
  'FAILED_TO_ACQUIRE_COPY_GRADE_LOCK',
  'MISSING_HANDWRITTEN_COPY_ID_FOR_OCR',
  'MISSING_RUN_ID_FOR_ANALYSIS',
  'MISSING_HANDWRITTEN_COPY_ID_FOR_CORRECTION',
  'NO_COPY_SELECTED',
  'COPIES_NOT_FOUND_OR_INACCESSIBLE',
  'NO_DOWNLOADABLE_PDF',
  'UNABLE_TO_GENERATE_SIGNED_URL',
  'RUBRIC_JSON_NOT_FOUND',
  'QUESTION_NOT_FOUND_IN_RUBRIC',
  'RUBRIC_ITEM_NOT_FOUND',
  'RUBRIC_ITEM_POINTS_OUT_OF_RANGE',
  'RUBRIC_LOCKED_RESULTS_PUBLISHED',
  'RUBRIC_BUSY',
  'IMPORT_SOURCE_NOT_FOUND',
  'IMPORT_SOURCE_HAS_NO_RUBRIC',
  'IMPORT_SOURCE_INCONSISTENT',
  'IMPORT_TARGET_NOT_ELIGIBLE',
  'IMPORT_TARGET_HAS_STRUCTURE',
  'IMPORT_TARGET_HAS_CORRECTED_COPIES',
  'STRUCTURE_SHAPE_LOCKED',
  'COPY_NOT_FOUND',
  'COPY_HAS_NO_STUDENT',
  'JUSTIFICATION_NOT_FOUND',
  'ADDITIONAL_DOCUMENTS_ALREADY_EXISTS',
  'INSUFFICIENT_CORRECTION_CREDITS',
  'CORRECTION_NOT_STARTED',
  'AI_CORRECTION_DISABLED',
  'SUBMISSION_DEADLINE_PASSED',
  'ATTEMPT_ALREADY_SUBMITTED',
  'EXERCISE_NOT_FOUND',
  'EXERCISES_NOT_FOUND',
  'CHAPTER_NOT_FOUND',
  'MODULE_NOT_FOUND',
  'RECIPIENT_NOT_FOUND',
  'FOLDER_NOT_FOUND',
  'DOCUMENT_NOT_FOUND',
  'DOCUMENT_LOCKED',
  'DOCUMENT_POSITION_ALREADY_EXISTS',
  'FAILED_TO_CREATE_DOCUMENT',
  'SECTION_NOT_FOUND',
  'SECTION_POSITION_ALREADY_EXISTS',
  'DOCUMENT_NOT_PUBLISHED',
  'DOCUMENT_NOT_ACCESSIBLE',
  'ANNALE_NOT_FOUND',
  'BAC_ANNALE_ACTIVITY_NOT_FOUND',
  'ANNALE_VERSION_NOT_AVAILABLE',
  'ALREADY_UNLOCKED',
  'BAC_ANNALES_PREMIUM_REQUIRED',
  'BAC_ANNALES_PERMISSION_REQUIRED',
  'ZIP_GENERATION_FAILED',
  'ZIP_STREAM_ERROR',
  'PYTHON_API_TIMEOUT',
  'PYTHON_API_ERROR',
  'PYTHON_EXECUTION_NO_RESULT',
  'INVALID_SCRIPTS_PATH',
  'LAMBDA_INVOKE_FAILED',
  'PDF_GENERATION_FAILED',
  'NO_QUESTIONS_FOUND',
  'EXERCISE_IMPORT_VALIDATION_FAILED',
  'NO_ANSWERS_FOUND',
  'NO_INPUTS_FOUND',
  'NO_EXERCISES_FOUND',
  'ONLY_ONE_EXERCISE_ALLOWED',
  'NO_CHAPTERS_FOUND',
  'IMPORT_VALIDATION_FAILED',
  'INVALID_USER_TYPE',
  'USER_NOT_FOUND',
  'INVALID_SCALE_MATCH',
  'UNIQUE_CONSTRAINT_FAILED',
  'RECORD_NOT_FOUND',
  'GRADED_STRUCTURE_NOT_FOUND',
  'ONLY_ONE_CHAPTER_ALLOWED',
  'ZIP_REQUESTS_LIMIT_EXCEEDED',
  'REPORT_PROCESSING_FAILED',
  'NO_ACCOUNTS_TO_CREATE',
  'SUBSCRIPTION_REQUIRED_FOR_CREDITS',
  'SLACK_MISSING_SIGNATURE_HEADERS',
  'SLACK_TIMESTAMP_INVALID',
  'SLACK_SIGNATURE_INVALID',
  'INVALID_AUTHENTICATION',
  'REGRADE_ALREADY_PENDING',
  'REGRADE_NOT_FOUND',
  'REGRADE_NOT_PENDING',
  'REGRADE_QUESTION_INVALID',
  'ASSIGNED_STUDENT_NOT_IN_CLASS',
  'ASSIGNED_STUDENT_HAS_WORK',
  'ATTEMPTS_LOCKED_AFTER_PUBLICATION',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/** Runtime map `{ CODE: 'CODE' }` for typed throws — derived from `API_ERROR_CODES`. */
export const API_ERROR = Object.fromEntries(API_ERROR_CODES.map((code) => [code, code])) as {
  readonly [K in ApiErrorCode]: K;
};

export interface ApiErrorResponseBody {
  statusCode: number;
  message?: ApiErrorCode | string;
  details?: unknown;
}

/** LaTeX compile error fields attached to `PDF_GENERATION_FAILED` HTTP details. */
export interface LatexErrorDetails {
  line?: number;
  wrongContent?: string;
  context?: string;
}

export function parseLatexErrorDetails(details: unknown): LatexErrorDetails | undefined {
  if (!details || typeof details !== 'object') return undefined;

  const d = details as Record<string, unknown>;
  const parsed: LatexErrorDetails = {
    line: typeof d.line === 'number' ? d.line : undefined,
    wrongContent: typeof d.wrongContent === 'string' ? d.wrongContent : undefined,
    context: typeof d.context === 'string' ? d.context : undefined,
  };

  if (parsed.line != null || parsed.wrongContent || parsed.context) {
    return parsed;
  }

  return undefined;
}
